import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import { createPortal } from 'react-dom';
import Modal from '../LazyModal';
import * as styles from './video-player.module.css';
import * as ctaStyles from '../CtaLink/CtaLink.module.css';
import PlayArrow from '../PlayArrow';
import CloseButton from '../CloseButton';
import { useBreakpoint } from 'gatsby-plugin-breakpoints';

import ReactPlayer from '../LazyReactPlayer';

const MOBILE_MEDIA_QUERY = '(max-width: 767px)';
const PLAYER_Z_INDEX = 2147483000;

const WISTIA_PLAYER_CONFIG = {
    options: {
        autoPlay: true,
        fullscreenButton: true,
        playbar: true,
        controlsVisibleOnLoad: true,
        doNotTrack: true,
    },
};

const REACT_PLAYER_CONFIG = {
    wistia: WISTIA_PLAYER_CONFIG,
    youtube: {
        playerVars: {
            playsinline: 1,
        },
    },
    vimeo: {
        playerOptions: {
            playsinline: true,
        },
    },
};

function getIsMobileViewport(): boolean {
    if (typeof window === 'undefined') {
        return false;
    }
    return window.matchMedia(MOBILE_MEDIA_QUERY).matches;
}

function parseWistiaId(url: string): string | null {
    const patterns = [
        /wistia\.(?:com|net)\/medias\/([a-z0-9]+)/i,
        /embed\/iframe\/([a-z0-9]+)/i,
        /wi\.st\/([a-z0-9]+)/i,
    ];

    for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match?.[1]) {
            return match[1];
        }
    }

    return null;
}

function getWistiaEmbedSrc(videoId: string, autoPlay: boolean): string {
    const params = new URLSearchParams({
        autoPlay: autoPlay ? 'true' : 'false',
        playsinline: 'true',
        fullscreenButton: 'true',
        controlsVisibleOnLoad: 'true',
        fitStrategy: 'contain',
        doNotTrack: 'true',
    });

    return `https://fast.wistia.net/embed/iframe/${videoId}?${params.toString()}`;
}

interface Props {
    src: string;
    thumbnail?: string | boolean;
    trigger?: (openModal: () => void) => React.ReactNode;
    ctaLabel?: string;
    ariaLabel?: string;
    onOpenChange?: (open: boolean) => void;
}

function useBodyScrollLock(locked: boolean) {
    const scrollYRef = useRef(0);

    useLayoutEffect(() => {
        if (!locked || typeof document === 'undefined') {
            return undefined;
        }

        scrollYRef.current = window.scrollY;
        const { style } = document.body;
        const previous = {
            overflow: style.overflow,
            position: style.position,
            top: style.top,
            width: style.width,
        };

        style.overflow = 'hidden';
        style.position = 'fixed';
        style.top = `-${scrollYRef.current}px`;
        style.width = '100%';

        return () => {
            style.overflow = previous.overflow;
            style.position = previous.position;
            style.top = previous.top;
            style.width = previous.width;
            window.scrollTo(0, scrollYRef.current);
        };
    }, [locked]);
}

const VideoPlayer: React.FC<Props> = ({
    src,
    thumbnail = true,
    trigger,
    ctaLabel,
    ariaLabel,
    onOpenChange,
}) => {
    const breakpoints = useBreakpoint();
    const isMobileLayout = !breakpoints.md;
    const wistiaId = parseWistiaId(src);
    const [modalOpen, setModalOpen] = useState(false);
    const [useMobileFullscreen, setUseMobileFullscreen] = useState(false);
    const [mobileWistiaEmbedSrc, setMobileWistiaEmbedSrc] = useState<
        string | null
    >(null);
    const [desktopPlaying, setDesktopPlaying] = useState(false);
    const didMountRef = useRef(false);

    useBodyScrollLock(modalOpen);

    useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true;
            return;
        }
        onOpenChange?.(modalOpen);
    }, [modalOpen, onOpenChange]);

    const openModal = useCallback(() => {
        const mobile = getIsMobileViewport();
        setUseMobileFullscreen(mobile);
        setDesktopPlaying(!mobile);
        setMobileWistiaEmbedSrc(
            mobile && wistiaId ? getWistiaEmbedSrc(wistiaId, true) : null
        );
        setModalOpen(true);
    }, [wistiaId]);

    const closeModal = useCallback(() => {
        setModalOpen(false);
        setDesktopPlaying(false);
        setMobileWistiaEmbedSrc(null);
    }, []);

    const handleCloseClick = (
        _event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        closeModal();
    };

    const handlePlayerError = () => {
        setDesktopPlaying(false);
    };

    const customModalStyles = {
        overlay: {
            zIndex: PLAYER_Z_INDEX,
            backgroundColor: 'rgba(0, 0, 0, 0.92)',
        },
    };

    const previewPlayer = (
        <div
            className={styles.wrapper}
            style={{
                paddingTop: '56.25%',
            }}
        >
            <ReactPlayer
                url={src}
                light={thumbnail}
                playIcon={
                    <div className={styles.playArrow}>
                        <PlayArrow heightWidth="4rem" strokeColor="#ffffff" />
                    </div>
                }
                style={{
                    position: 'absolute',
                    top: '0',
                    left: '0',
                }}
                height="100%"
                width="100%"
                onClickPreview={openModal}
            />
        </div>
    );

    const mobileOverlay =
        modalOpen && useMobileFullscreen ? (
            <div
                className={styles.fullscreenOverlay}
                role="dialog"
                aria-modal="true"
                aria-label="Video Player"
            >
                <div className={styles.fullscreenClose}>
                    <CloseButton
                        visualStyle="orange"
                        onClick={handleCloseClick}
                    />
                </div>
                <div className={styles.fullscreenPlayer}>
                    {mobileWistiaEmbedSrc ? (
                        <iframe
                            key={mobileWistiaEmbedSrc}
                            src={mobileWistiaEmbedSrc}
                            title={ariaLabel || 'Video'}
                            className={styles.fullscreenIframe}
                            allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
                            allowFullScreen
                        />
                    ) : (
                        <ReactPlayer
                            url={src}
                            width="100%"
                            height="100%"
                            controls
                            playsinline
                            config={REACT_PLAYER_CONFIG}
                            onError={handlePlayerError}
                        />
                    )}
                </div>
            </div>
        ) : null;

    const ctaButton = ctaLabel ? (
        <button
            type="button"
            className={`${ctaStyles.ctaLink} ${ctaStyles.outlineLight}`}
            aria-label={ariaLabel || `Play video: ${ctaLabel}`}
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openModal();
            }}
        >
            {ctaLabel}
        </button>
    ) : null;

    return (
        <>
            {ctaButton || (trigger ? trigger(openModal) : previewPlayer)}
            {typeof document !== 'undefined' && mobileOverlay
                ? createPortal(mobileOverlay, document.body)
                : null}
            <Modal
                isOpen={modalOpen && !useMobileFullscreen}
                onRequestClose={closeModal}
                contentLabel="Video Player"
                className={`${styles.modalContent} ${
                    isMobileLayout
                        ? styles.mobilemodalContent
                        : styles.desktopmodalContent
                }`}
                style={customModalStyles}
                ariaHideApp={false}
            >
                <div className={styles.closeButton}>
                    <CloseButton
                        visualStyle="orange"
                        onClick={handleCloseClick}
                    />
                </div>

                <div
                    className={styles.modalPlayerWrapper}
                    style={{
                        paddingTop: '56.25%',
                    }}
                >
                    <ReactPlayer
                        url={src}
                        style={{
                            position: 'absolute',
                            top: '0',
                            left: '0',
                        }}
                        height={isMobileLayout ? '100%' : '80vh'}
                        width="100%"
                        controls
                        playing={desktopPlaying}
                        config={REACT_PLAYER_CONFIG}
                        onError={handlePlayerError}
                    />
                </div>
            </Modal>
        </>
    );
};

export default VideoPlayer;
